import React, { useEffect, useMemo, useState, useRef } from 'react';
import EmployeeLayout from '../components/EmployeeLayout';
import { getEmployeeProfile } from '../services/employeeService';
import { getMyPayrollRecord, getManualPayslipUrl } from '../services/payrollService';
import { getPayslipSettings, fetchPayslipLogoObjectUrl } from '../services/payslipSettingsService';
import { PayslipDocument, downloadElementAsPdf } from '../components/PayslipDocument';
import { apiFetch } from '../utils/apiClient';
import '../styles/tailwind.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Used until the client's own payslip settings finish loading (or if a client hasn't
// configured any yet) - only the company header is read from it here, the pay itself comes
// from the processed payroll record.
const DEFAULT_PAYSLIP_SETTINGS = {
  companyName: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  phone: '',
  website: '',
  email: '',
};

const buildEmployeeName = (employee) =>
  `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || employee?.name || 'N/A';

const getPayrollCreditStatus = (record) =>
  record.creditStatus ?? record.paymentStatus ?? record.payrollStatus ?? record.employee?.creditStatus ?? record.status ?? '';

const isAmountCredited = (status) => {
  const value = String(status || '').trim().toLowerCase();
  return ['amount credited', 'credited', 'amount_credited', 'paid', 'payment credited'].includes(value);
};

const getPayrollManualFlag = (record) => Boolean(record.manualPayslip);
const getPayrollHasFile = (record) => Boolean(record.hasPayslipFile);

function PayslipGeneratorPage({ userId, userName, onLogout }) {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [checkingPayroll, setCheckingPayroll] = useState(false);
  const [payrollMessage, setPayrollMessage] = useState('');
  const [manualPayslipPayrollId, setManualPayslipPayrollId] = useState(null);
  const [downloadingManualPayslip, setDownloadingManualPayslip] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [payrollRecord, setPayrollRecord] = useState(null);
  const [payslipSettings, setPayslipSettings] = useState(DEFAULT_PAYSLIP_SETTINGS);
  const [logoUrl, setLogoUrl] = useState(null);
  const payslipRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    const loadData = async () => {
      setLoading(true);
      try {
        const [employeeData, settingsData] = await Promise.all([
          getEmployeeProfile(userId),
          getPayslipSettings().catch((err) => {
            console.error('Failed to load payslip settings:', err);
            return DEFAULT_PAYSLIP_SETTINGS;
          }),
        ]);

        if (cancelled) return;
        setEmployee(employeeData);
        setPayslipSettings({ ...DEFAULT_PAYSLIP_SETTINGS, ...settingsData });
        if (settingsData?.hasLogo) {
          objectUrl = await fetchPayslipLogoObjectUrl();
          if (!cancelled) setLogoUrl(objectUrl);
        }
        setError('');
      } catch (err) {
        console.error('Failed to load payslip generator data:', err);
        if (!cancelled) setError('Unable to load payslip data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (userId) {
      loadData();
    }

    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [userId]);

  useEffect(() => {
    setPayrollMessage('');
    setManualPayslipPayrollId(null);
    setPayrollRecord(null);
  }, [selectedMonth, selectedYear]);

  const handleGeneratePayslip = async () => {
    if (!employee) return;

    setCheckingPayroll(true);
    setManualPayslipPayrollId(null);
    setPayrollRecord(null);
    setPayrollMessage('');
    setError('');

    try {
      const employeePayroll = await getMyPayrollRecord({ month: selectedMonth, year: selectedYear });

      if (employeePayroll && isAmountCredited(getPayrollCreditStatus(employeePayroll))) {
        if (getPayrollManualFlag(employeePayroll)) {
          if (getPayrollHasFile(employeePayroll)) {
            setManualPayslipPayrollId(employeePayroll.payrollId);
            setPayrollMessage('Payroll amount credited. Payslip is ready to download.');
          } else {
            setPayrollMessage('Payslip will be available once uploaded by admin.');
          }
          return;
        }

        setPayrollRecord(employeePayroll);
        setPayrollMessage('Payroll amount credited. Payslip is ready to download.');
        return;
      }

      setPayrollMessage('Amount was not credited.');
    } catch (err) {
      console.error('Failed to verify payroll status:', err);
      setPayrollMessage('');
      setError(err.message || 'Unable to verify payroll status.');
    } finally {
      setCheckingPayroll(false);
    }
  };

  const handleDownloadManualPayslip = async () => {
    if (!manualPayslipPayrollId) return;
    setDownloadingManualPayslip(true);
    try {
      const response = await apiFetch(getManualPayslipUrl(manualPayslipPayrollId));
      if (!response.ok) throw new Error('Failed to download payslip');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'Payslip.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Failed to download payslip:', err);
      setError(err.message || 'Failed to download payslip');
    } finally {
      setDownloadingManualPayslip(false);
    }
  };

  // Everything pay-related comes from the processed payroll record - the earnings/deductions
  // rows were computed from attendance at payroll run time (and possibly adjusted by the admin),
  // so the payslip shows exactly what was paid. LOP is already deducted under "Other
  // Deductions", so each earning is shown at its full amount in both columns.
  const payslip = useMemo(() => {
    if (!employee || !payrollRecord) return null;

    const earnings = (payrollRecord.earnings || []).map((item) => ({
      label: item.label,
      actual: Number(item.amount) || 0,
      earned: Number(item.amount) || 0,
    }));
    const deductions = (payrollRecord.deductions || []).map((item) => ({
      label: item.label,
      amount: Number(item.amount) || 0,
    }));
    const totalEarned = earnings.reduce((sum, item) => sum + item.earned, 0);
    const totalDeductions = deductions.reduce((sum, item) => sum + item.amount, 0);
    const variablePay = Number(payrollRecord.variablePay) || 0;
    const workingDays = Number(payrollRecord.workingDays) || 0;
    const lop = Number(payrollRecord.lop) || 0;

    return {
      employeeName: buildEmployeeName(employee),
      employeeCode: employee.employeeId,
      monthLabel: MONTH_NAMES[selectedMonth - 1],
      year: selectedYear,
      designation: employee.designation || 'N/A',
      location: employee.workLocation || 'N/A',
      department: employee.department || 'N/A',
      grade: employee.employeeCategory || 'N/A',
      panNumber: employee.panNumber || payrollRecord.panNumber || 'N/A',
      gender: employee.gender || 'N/A',
      doj: employee.dateOfJoining || payrollRecord.dateOfJoining || 'N/A',
      workingDays,
      lop,
      payableDays: payrollRecord.payableDays ?? Math.max(workingDays - lop, 0),
      pfNumber: employee.pfNumber || payrollRecord.pf || 'N/A',
      uanNumber: employee.uanNumber || payrollRecord.uan || 'N/A',
      bankName: employee.bankName || 'N/A',
      bankAccount: employee.accountNumber || payrollRecord.accountNumber || 'N/A',
      earnings,
      deductions,
      variablePay,
      totalActual: totalEarned,
      totalEarned,
      totalDeductions,
      netPay: payrollRecord.netSalary ?? Math.max(totalEarned + variablePay - totalDeductions, 0),
    };
  }, [employee, payrollRecord, selectedMonth, selectedYear]);

  const yearOptions = Array.from({ length: 11 }, (_, index) => new Date().getFullYear() - 5 + index);

  const handleDownloadPdf = async () => {
    if (!payslip) return;
    setDownloadingPdf(true);
    try {
      await downloadElementAsPdf(payslipRef.current, `Payslip_${payslip.monthLabel}_${payslip.year}.pdf`);
    } catch (err) {
      console.error('Failed to download payslip:', err);
      setError('Failed to download the payslip PDF.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const canShowPayslip = Boolean(payrollRecord);

  return (
    <EmployeeLayout
      userName={userName}
      onLogout={onLogout}
      activeItem="payslip"
      title="Payslip"
      subtitle="Generate your payslip after payroll amount is credited for the selected month."
    >
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-end gap-4 rounded-xl border border-border/80 bg-card p-4 shadow-sm">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground">Employee ID</label>
                <div className="flex h-9 items-center rounded-lg border border-border bg-muted/40 px-3 text-sm text-foreground">
                  {employee?.employeeId || 'N/A'}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-foreground">Employee Name</label>
                <div className="flex h-9 items-center rounded-lg border border-border bg-muted/40 px-3 text-sm text-foreground">
                  {employee ? buildEmployeeName(employee) : userName || 'N/A'}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="payMonth" className="text-sm font-medium text-foreground">Month</label>
                <select
                  id="payMonth"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="h-9 rounded-lg border border-border bg-white px-2.5 text-sm outline-none focus:border-employee focus:ring-2 focus:ring-employee/30"
                >
                  {MONTH_NAMES.map((month, index) => (
                    <option key={month} value={index + 1}>{month}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="payYear" className="text-sm font-medium text-foreground">Year</label>
                <select
                  id="payYear"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="h-9 rounded-lg border border-border bg-white px-2.5 text-sm outline-none focus:border-employee focus:ring-2 focus:ring-employee/30"
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleGeneratePayslip}
                disabled={loading || checkingPayroll || !employee}
                className="h-9 rounded-lg bg-employee px-4 text-sm font-medium text-employee-foreground hover:bg-employee/90 disabled:opacity-60"
              >
                {checkingPayroll ? 'Checking...' : 'Generate Payslip'}
              </button>
            </div>

            {loading ? (
              <p className="text-sm text-muted-foreground">Loading payslip data...</p>
            ) : error ? (
              <p className="text-sm text-[#b91c1c]">{error}</p>
            ) : !employee ? (
              <p className="text-sm text-muted-foreground">No employee available for payslip generation.</p>
            ) : payrollMessage && !canShowPayslip && !manualPayslipPayrollId ? (
              <p className="text-sm text-[#b91c1c]">{payrollMessage}</p>
            ) : payrollMessage && (canShowPayslip || manualPayslipPayrollId) ? (
              <div className="rounded-lg border border-[#bbf7d0] bg-[#f0fdf4] px-3 py-2 text-sm text-[#15803d]">{payrollMessage}</div>
            ) : (
              <p className="text-sm text-muted-foreground">Select month and year, then click Generate Payslip.</p>
            )}

            {manualPayslipPayrollId && (
              <button
                type="button"
                onClick={handleDownloadManualPayslip}
                disabled={downloadingManualPayslip}
                className="h-9 w-fit rounded-lg bg-employee px-4 text-sm font-medium text-employee-foreground hover:bg-employee/90 disabled:opacity-60"
              >
                {downloadingManualPayslip ? 'Downloading...' : 'Download Payslip'}
              </button>
            )}

            {payslip && (
              <>
                <PayslipDocument ref={payslipRef} settings={payslipSettings} logoUrl={logoUrl} payslip={payslip} />

                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="h-9 w-fit rounded-lg bg-employee px-4 text-sm font-medium text-employee-foreground hover:bg-employee/90 disabled:opacity-60"
                >
                  {downloadingPdf ? 'Preparing PDF...' : 'Download Payslip PDF'}
                </button>
              </>
            )}
          </div>
    </EmployeeLayout>
  );
}

export default PayslipGeneratorPage;
